// Address checks per chain family. The api accepts these forms as `dst_recipient` / `user`.

import { PublicKey } from "@solana/web3.js";
import type { Family } from "./chains";

const BASE58 = /^[1-9A-HJ-NP-Za-km-z]+$/;

/** A reason the address is not valid for `family`, or null when it is. `jupnet`: the Jupnet account
 *  a gum_deposit credits, an ed25519 public key in base58 (Solana's form; never the zero key). */
export function recipientError(family: Family | "jupnet", addr: string): string | null {
  const a = addr.trim();
  if (!a) return "enter a recipient address";
  switch (family) {
    case "jupnet":
      if (!BASE58.test(a)) return "a Jupnet account is a base58 public key";
      try {
        const b = new PublicKey(a).toBytes();
        return b.every((x) => x === 0) ? "the zero key is not a Jupnet account" : null;
      } catch {
        return "a Jupnet account is a 32-byte base58 public key";
      }
    case "evm":
      return /^0x[0-9a-fA-F]{40}$/.test(a) ? null : "an EVM address is 0x followed by 40 hex characters";
    case "solana":
      if (!BASE58.test(a)) return "a Solana address is base58";
      try {
        return new PublicKey(a).toBytes().length === 32 ? null : "a Solana address is 32 bytes";
      } catch {
        return "not a valid Solana address";
      }
    case "sui":
      return /^0x[0-9a-fA-F]{64}$/.test(a) ? null : "a Sui address is 0x followed by 64 hex characters";
  }
}

/** A token address check for a custom destination token. */
export function tokenError(family: Family, addr: string): string | null {
  if (family === "sui") return /^0x[0-9a-fA-F]+::\w+::\w+$/.test(addr.trim()) ? null : "a Sui coin type looks like 0x…::module::NAME";
  return recipientError(family, addr) ? `not a valid ${family === "evm" ? "EVM token" : "SPL mint"} address` : null;
}

/** Token decimals: an EVM/SPL/Sui token's are a u8, and 36 is well past any real one. */
export const MAX_DECIMALS = 36;
const validDecimals = (d: number) => Number.isInteger(d) && d >= 0 && d <= MAX_DECIMALS;

/** A custom token's decimals field → its value, or null unless a whole number in 0..MAX_DECIMALS. */
export function parseDecimals(s: string): number | null {
  const t = s.trim();
  if (!/^\d{1,2}$/.test(t)) return null;
  const d = Number(t);
  return validDecimals(d) ? d : null;
}

/** Decimal string → smallest-unit integer string; null when malformed or more precise than `decimals`. */
export function toBaseUnits(amount: string, decimals: number): string | null {
  if (!validDecimals(decimals)) return null;
  const a = amount.trim();
  if (!/^\d*(\.\d*)?$/.test(a) || a === "" || a === ".") return null;
  const [w = "", f = ""] = a.split(".");
  if (f.length > decimals) return null;
  const v = BigInt(w || "0") * 10n ** BigInt(decimals) + BigInt((f + "0".repeat(decimals)).slice(0, decimals) || "0");
  return v > 0n ? v.toString() : null;
}

/** Smallest units → a decimal string at full precision (trailing zeros dropped), or `maxFrac` places.
 *  Invalid decimals render "—" rather than throw: the value comes from an editable field. */
export function fromBaseUnits(v: string | bigint, decimals: number, maxFrac = decimals): string {
  if (!validDecimals(decimals)) return "—";
  const n = BigInt(v);
  const d = 10n ** BigInt(decimals);
  const whole = n / d;
  let frac = (n % d).toString().padStart(decimals, "0").slice(0, maxFrac).replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole.toString();
}
